// src/components/RealProgressGraph.js (New File)
import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { Line } from 'react-chartjs-2'; 
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';

// Register Chart.js components if not done globally
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const RealProgressGraph = () => {
    const [progressData, setProgressData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const currentUser = auth.currentUser;

    useEffect(() => {
        const fetchProgressData = async () => {
            if (!currentUser) {
                setLoading(false);
                return;
            }

            try {
                // Query the 'dailyProgress' subcollection for the last 30 entries by date
                const progressCollectionRef = collection(db, 'progress', currentUser.uid, 'dailyProgress');
                
                // Note: The date field is the document ID (e.g., '2025-10-20'), so ordering by doc ID is essentially ordering by date string.
                const q = query(
                    progressCollectionRef, 
                    orderBy('date', 'desc'), 
                    limit(30)
                );
                
                const querySnapshot = await getDocs(q);
                
                const fetchedData = querySnapshot.docs.map(doc => doc.data());
                
                // Sort by date ascending for the graph (since we fetched descending)
                const sortedData = fetchedData.sort((a, b) => new Date(a.date) - new Date(b.date));

                setProgressData(sortedData);
                setLoading(false);

            } catch (err) {
                console.error('Error fetching progress data:', err);
                setError('Failed to load progress graph data.');
                setLoading(false);
            }
        };

        fetchProgressData();
    }, [currentUser]);

    if (loading) {
        return <div className="card progress-graph-card">Loading progress graph...</div>;
    }

    if (error) {
        return <div className="card progress-graph-card error-message">{error}</div>;
    }

    // --- Prepare Chart Data ---
    const labels = progressData.map(data => new Date(data.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }));
    // Filter out data points where dietScore is not a valid number
    const chartDataPoints = progressData.map(data => 
        data.dietScore && !isNaN(parseInt(data.dietScore)) ? parseInt(data.dietScore) : null
    );

    const data = {
        labels: labels,
        datasets: [
            {
                label: 'Diet Score (1-5)',
                data: chartDataPoints,
                fill: false,
                backgroundColor: 'rgb(75, 192, 192)',
                borderColor: 'rgba(75, 192, 192, 0.8)',
                tension: 0.3,
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'top' },
            title: { display: true, text: 'Diet Score Progress (Last 30 Days)' },
        },
        scales: {
            y: {
                min: 0,
                max: 5,
                ticks: {
                    stepSize: 1
                },
                title: {
                    display: true,
                    text: 'Score'
                }
            }
        }
    };

    if (progressData.length === 0) {
        return <div className="card progress-graph-card"><p>No progress data submitted yet. Track your progress on the 'My Progress' page!</p></div>;
    }

    return (
        <div className="card progress-graph-card">
            <h3>📅 Progress Graph</h3>
            <div style={{ height: '300px' }}> {/* Ensure the container has a defined height */}
                <Line data={data} options={options} />
            </div>
        </div>
    );
};

export default RealProgressGraph;